import React, { useEffect, useMemo, useState } from "react";

type AnimatedTextProps = {
  children: React.ReactNode;
  speed?: number;
  className?: string;
};

const getTextFromChildren = (children: React.ReactNode): string => {
  let text = "";

  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += child;
    } else if (React.isValidElement(child)) {
      text += getTextFromChildren(child.props.children);
    }
  });

  return text;
};

const renderAnimatedChildren = (
  children: React.ReactNode,
  visibleText: string,
  cursor: { index: number },
): React.ReactNode => {
  return React.Children.map(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      const text = String(child);
      const start = cursor.index;
      const end = start + text.length;

      cursor.index = end;

      return visibleText.slice(start, Math.min(end, visibleText.length));
    }

    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        ...child.props,
        children: renderAnimatedChildren(
          child.props.children,
          visibleText,
          cursor,
        ),
      });
    }

    return child;
  });
};

const AnimatedText = ({
  children,
  speed = 100,
  className = "",
}: AnimatedTextProps) => {
  const [count, setCount] = useState(0);

  const fullText = useMemo(() => getTextFromChildren(children), [children]);

  useEffect(() => {
    setCount(0);

    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev >= fullText.length) {
          clearInterval(interval);
          return prev;
        }

        return prev + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [fullText, speed]);

  return (
    <span className={`AnimatedText__ ${className}`}>
      {renderAnimatedChildren(children, fullText.slice(0, count), {
        index: 0,
      })}
      {count < fullText.length && <span className="AnimatedText__cursor" />}
    </span>
  );
};

export default AnimatedText;
